'use client';

import type { CmsShellViewerMode } from '@/cms/chrome/shell-viewer-mode';
import { WorkspaceMobileDrawer } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-mobile-drawer';
import { useWorkspaceIsMobileViewport } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-viewport';
import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { formatBrowserDate } from '@/lib/format/browser-date-time';
import {
  WORKSPACE_UPDATES_READ_KEY,
  type WorkspaceUpdate,
  workspaceReadUpdatesSchema,
  workspaceUpdatesSchema,
} from '@/lib/site/workspace-updates';
import { SiteRuntimeActionTrigger } from '@/site-runtime/components/site-runtime-entry-action-controller';
import { More1Fill } from '@mingcute/react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useId, useState } from 'react';
import { CmsShellViewerState } from './cms-shell-viewer-state';
import { WorkspaceFeedbackDialog } from './cms-workspace-feedback-dialog';
import {
  SidebarLocaleMenu,
  SidebarLocaleOptions,
} from './cms-workspace-sidebar-locale-menu';
import { SidebarPopover } from './cms-workspace-sidebar-popover';
import styles from './cms-workspace-sidebar-tools.module.css';

export function CmsWorkspaceSidebarTools({
  locale,
  currentPath,
  expanded,
  viewerMode,
}: {
  locale: string;
  currentPath: string;
  expanded: boolean;
  viewerMode: CmsShellViewerMode;
}) {
  const t = useTranslations('Common.workspaceSidebar');
  const common = useTranslations('Common');
  const isMobile = useWorkspaceIsMobileViewport();
  const [mobilePanel, setMobilePanel] = useState<
    'more' | 'language' | 'updates' | null
  >(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const drawerId = useId();
  const [updates, setUpdates] = useState<WorkspaceUpdate[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [readIds, setReadIds] = useState<string[]>([]);
  const [storageFailed, setStorageFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setUpdates(null);
    setFailed(false);
    void (async () => {
      try {
        const response = await fetch(
          `/api/workspace/updates?locale=${encodeURIComponent(locale)}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error('Updates unavailable');
        const data = workspaceUpdatesSchema.parse(await response.json());
        if (!controller.signal.aborted) setUpdates(data.updates);
      } catch {
        if (!controller.signal.aborted) setFailed(true);
      }
    })();
    return () => controller.abort();
  }, [locale, attempt]);

  useEffect(() => {
    const read = () => {
      try {
        const value = localStorage.getItem(WORKSPACE_UPDATES_READ_KEY);
        setReadIds(
          value ? workspaceReadUpdatesSchema.parse(JSON.parse(value)) : []
        );
        setStorageFailed(false);
      } catch {
        setStorageFailed(true);
      }
    };
    read();
    const sync = (event: StorageEvent) => {
      if (event.key === null || event.key === WORKSPACE_UPDATES_READ_KEY)
        read();
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const markRead = useCallback(
    (ids: string[]) => {
      try {
        const saved = localStorage.getItem(WORKSPACE_UPDATES_READ_KEY);
        const parsed = saved
          ? workspaceReadUpdatesSchema.safeParse(JSON.parse(saved))
          : null;
        const next = (updates ?? [])
          .map((item) => item.id)
          .filter(
            (id) =>
              ids.includes(id) ||
              readIds.includes(id) ||
              (parsed?.success && parsed.data.includes(id))
          );
        localStorage.setItem(WORKSPACE_UPDATES_READ_KEY, JSON.stringify(next));
        setReadIds(next);
        setStorageFailed(false);
      } catch {
        setStorageFailed(true);
      }
    },
    [updates, readIds]
  );

  const hasUnread =
    updates?.some((item) => !readIds.includes(item.id)) ?? false;
  const renderUpdates = (close: () => void) => (
    <>
      <div className={styles.updatesHeader}>
        <strong>{t('whatsNew')}</strong>
        <button
          type="button"
          className={styles.textButton}
          disabled={!hasUnread}
          onClick={() => markRead((updates ?? []).map((item) => item.id))}
        >
          {t('markAllRead')}
        </button>
      </div>
      {storageFailed ? (
        <p role="alert" className={styles.error}>
          {t('storageFailed')}
        </p>
      ) : null}
      {failed ? (
        <div className={styles.state} role="alert">
          {t('updatesFailed')}
          <button
            type="button"
            className={styles.textButton}
            onClick={() => setAttempt((value) => value + 1)}
          >
            {t('retry')}
          </button>
        </div>
      ) : updates === null ? (
        <output className={styles.state}>{common('loading')}</output>
      ) : updates.length === 0 ? (
        <p className={styles.state}>{t('emptyUpdates')}</p>
      ) : (
        <ul className={styles.updatesList}>
          {updates.map((item) => (
            <li key={item.id}>
              <WorkspaceUpdateItem
                item={item}
                read={readIds.includes(item.id)}
                onRead={() => {
                  markRead([item.id]);
                  if (item.action) close();
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  );
  const renderMore = (close: () => void) => (
    <div className={styles.mobileMenu} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      {mobilePanel === null || mobilePanel === 'more' ? (
        <>
          <button
            type="button"
            className={styles.mobileMenuItem}
            aria-label={t('language')}
            onClick={() => setMobilePanel('language')}
          >
            {renderAppIcon({ name: 'Globe', className: styles.icon })}
            {t('language')}
          </button>
          <button
            type="button"
            className={styles.mobileMenuItem}
            data-sidebar-feedback-action=""
            aria-label={t('feedback')}
            onClick={() => {
              close();
              setFeedbackOpen(true);
            }}
          >
            {renderAppIcon({
              name: 'service',
              className: styles.icon,
            })}
            {t('feedback')}
          </button>
          <button
            type="button"
            className={styles.mobileMenuItem}
            aria-label={t('whatsNew')}
            onClick={() => setMobilePanel('updates')}
          >
            {renderAppIcon({
              name: 'notification',
              className: styles.icon,
            })}
            {t('whatsNew')}
            {hasUnread && (
              <span className={styles.unreadDot} aria-label={t('unread')} />
            )}
          </button>
        </>
      ) : mobilePanel === 'language' ? (
        <SidebarLocaleOptions
          currentPath={currentPath}
          locale={locale}
          onNavigate={close}
        />
      ) : (
        renderUpdates(close)
      )}
    </div>
  );
  return (
    <div
      className={styles.tools}
      data-sidebar-tools=""
      data-expanded={expanded}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      {isMobile ? (
        <>
          <button
            type="button"
            className={styles.toolButton}
            aria-label={t('more')}
            aria-haspopup="dialog"
            aria-expanded={mobilePanel !== null}
            onClick={() => setMobilePanel('more')}
          >
            <More1Fill className={styles.moreIcon} aria-hidden="true" />
            {hasUnread && (
              <span className={styles.unreadDot} aria-label={t('unread')} />
            )}
          </button>
          {mobilePanel !== null && (
            <WorkspaceMobileDrawer
              id={drawerId}
              open
              onOpenChange={(open) => {
                if (!open) setMobilePanel(null);
              }}
              title={
                <div className={styles.dialogHeading}>
                  {mobilePanel !== 'more' && (
                    <button
                      type="button"
                      className={styles.closeButton}
                      aria-label={t('more')}
                      onClick={() => setMobilePanel('more')}
                    >
                      {renderAppIcon({
                        name: locale === 'ar' ? 'ChevronRight' : 'ChevronLeft',
                        className: styles.icon,
                      })}
                    </button>
                  )}
                  <span>
                    {t(
                      mobilePanel === 'language'
                        ? 'language'
                        : mobilePanel === 'updates'
                          ? 'whatsNew'
                          : 'more'
                    )}
                  </span>
                  <button
                    type="button"
                    className={styles.closeButton}
                    aria-label={t('close')}
                    onClick={() => setMobilePanel(null)}
                  >
                    {renderAppIcon({ name: 'X', className: styles.icon })}
                  </button>
                </div>
              }
              size="content"
            >
              {renderMore(() => setMobilePanel(null))}
            </WorkspaceMobileDrawer>
          )}
        </>
      ) : !expanded ? (
        <SidebarPopover
          label={t('more')}
          className={styles.toolButton}
          popoverClassName={styles.morePopover}
          trigger={
            <>
              <More1Fill className={styles.moreIcon} aria-hidden="true" />
              {hasUnread && (
                <span className={styles.unreadDot} aria-label={t('unread')} />
              )}
            </>
          }
        >
          {(closeMore) => (
            <div className={styles.mobileMenu}>
              <SidebarPopover
                label={t('language')}
                popoverClassName={styles.languagePopover}
                className={styles.mobileMenuItem}
                trigger={
                  <>
                    {renderAppIcon({ name: 'Globe', className: styles.icon })}
                    {t('language')}
                    {renderAppIcon({
                      name: 'ChevronRight',
                      className: styles.submenuArrow,
                    })}
                  </>
                }
              >
                {(close) => (
                  <SidebarLocaleOptions
                    currentPath={currentPath}
                    locale={locale}
                    onNavigate={() => {
                      close();
                      closeMore();
                    }}
                  />
                )}
              </SidebarPopover>
              <button
                type="button"
                className={styles.mobileMenuItem}
                data-sidebar-feedback-action=""
                aria-label={t('feedback')}
                onClick={() => {
                  closeMore();
                  setFeedbackOpen(true);
                }}
              >
                {renderAppIcon({ name: 'service', className: styles.icon })}
                {t('feedback')}
              </button>
              <SidebarPopover
                label={t('whatsNew')}
                className={styles.mobileMenuItem}
                trigger={
                  <>
                    {renderAppIcon({
                      name: 'notification',
                      className: styles.icon,
                    })}
                    {t('whatsNew')}
                    {hasUnread && (
                      <span
                        className={styles.unreadDot}
                        aria-label={t('unread')}
                      />
                    )}
                    {renderAppIcon({
                      name: 'ChevronRight',
                      className: styles.submenuArrow,
                    })}
                  </>
                }
              >
                {(close) =>
                  renderUpdates(() => {
                    close();
                    closeMore();
                  })
                }
              </SidebarPopover>
            </div>
          )}
        </SidebarPopover>
      ) : (
        <>
          <SidebarLocaleMenu
            currentPath={currentPath}
            locale={locale}
            label={t('language')}
          />
          <button
            type="button"
            className={styles.toolButton}
            data-sidebar-feedback-action=""
            aria-label={t('feedback')}
            title={t('feedback')}
            onClick={() => setFeedbackOpen(true)}
          >
            {renderAppIcon({ name: 'service', className: styles.icon })}
          </button>
          <SidebarPopover
            label={t('whatsNew')}
            className={styles.toolButton}
            trigger={
              <>
                {renderAppIcon({
                  name: 'notification',
                  className: styles.icon,
                })}
                {hasUnread ? (
                  <span className={styles.unreadDot} aria-label={t('unread')} />
                ) : null}
              </>
            }
          >
            {renderUpdates}
          </SidebarPopover>
        </>
      )}
      <CmsShellViewerState mode={viewerMode}>
        {({ currentUser, isPending }) => (
          <WorkspaceFeedbackDialog
            key={currentUser?.id ?? 'visitor'}
            open={feedbackOpen}
            onOpenChange={setFeedbackOpen}
            user={currentUser}
            disabled={isPending || viewerMode !== 'live'}
            locale={locale}
            currentPath={currentPath}
          />
        )}
      </CmsShellViewerState>
    </div>
  );
}

function WorkspaceUpdateItem({
  item,
  read,
  onRead,
}: {
  item: WorkspaceUpdate;
  read: boolean;
  onRead: () => void;
}) {
  const t = useTranslations('Common.workspaceSidebar');
  const descriptionId = useId();
  const attributes = {
    'data-read': String(read),
    'aria-label': `${item.title} — ${t(read ? 'read' : 'unread')}`,
    'aria-describedby': descriptionId,
  };
  const content = (
    <>
      <span className={styles.updateStatus} aria-hidden="true">
        {!read && <span className={styles.unreadDot} />}
      </span>
      <span className={styles.updateTitle}>{item.title}</span>
      <span className={styles.updateDescription} id={descriptionId}>
        {item.description}
      </span>
      <span className={styles.updateFooter}>
        <time className={styles.updateDate} dateTime={item.date}>
          {formatBrowserDate(item.date)}
        </time>
        {item.action ? (
          <span className={styles.updateArrow} aria-hidden="true">
            {renderAppIcon({
              name: 'ChevronRight',
              className: styles.updateActionIcon,
            })}
          </span>
        ) : null}
      </span>
    </>
  );

  return item.action ? (
    <SiteRuntimeActionTrigger
      action={item.action}
      clientNavigation={item.action.type === 'switch_to_workspace'}
      className={styles.update}
      dataAttributes={attributes}
      onClick={onRead}
    >
      {content}
    </SiteRuntimeActionTrigger>
  ) : (
    <button
      type="button"
      className={styles.update}
      {...attributes}
      onClick={onRead}
    >
      {content}
    </button>
  );
}
